/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '../../root'
import { UserGroupSteps } from '../workflows/UserGroupSteps'
import Modal from '../Modal'
import { LoadingModal } from './LoadingModal'
import type { DetailedWorkflow } from '../../services/api/workflows'

interface WorkflowApproversModalProps {
  workflowId: number
  workflow?: any
  onSuccess?: () => void
}

interface ApproverStep {
  processingType: string
  taskTeamStepLabel: string | number | null
  nofAllValue: number | null
  usersGroups: string[]
}

/**
 * Workflow Approvers Management Modal
 *
 * Allows managing approval sequence for a workflow.
 * Based on lego/page/workflows/approvers/Modal.jsx
 */
export const WorkflowApproversModal: React.FC<WorkflowApproversModalProps> = ({
  workflowId,
  workflow: initialWorkflow,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const context = useServiceContext()
  const { api } = context

  const [loading, setLoading] = useState(true)
  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(initialWorkflow || null)
  const [steps, setSteps] = useState<ApproverStep[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadWorkflowData()
  }, [workflowId])

  const loadWorkflowData = async () => {
    try {
      setLoading(true)

      // Load workflow details if not provided
      let wfData = initialWorkflow
      if (!wfData?.workflowNo) {
        wfData = await api.workflows.getWorkflow(workflowId)
      }

      setWorkflow(wfData)

      // Load approvers data
      const approversResponse = await api.workflows.getCRApprovers(wfData.id, true)

      // The raw API returns { reviewers: [...steps] } where reviewers is a flat array.
      // Handle both formats for safety (array directly or nested under .steps).
      const stepsData = Array.isArray(approversResponse.approvers)
        ? approversResponse.approvers
        : approversResponse.approvers?.steps

      // Transform API response to component format
      const approverSteps =
        stepsData?.map((step: any) => ({
          processingType: step.processingType,
          taskTeamStepLabel: step.taskTeamStepLabel,
          nofAllValue: step.nofAllValue,
          usersGroups:
            step.taskTeams?.map((tt: any) => {
              const id = tt.user?.id || tt.group?.id
              const type = tt.user ? 'USER' : 'GROUP'
              return `${type}-${id}`
            }) || []
        })) || []

      setSteps(approverSteps)
      setLoading(false)
    } catch (error) {
      console.error('Failed to load workflow approvers:', error)
      setErrors({ submit: t('ERROR_LOADING_DATA') || 'Failed to load data' })
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setIsSubmitting(true)

      // Transform data for API
      const updateData = {
        approvers: steps.map((step) => ({
          processingType: step.processingType,
          taskTeamStepLabel: step.taskTeamStepLabel ?? null,
          nofAllValue: step.processingType === 'N_OF_ALL' ? step.nofAllValue : null,
          taskTeams: step.usersGroups.map((uid) => {
            const [type, id] = uid.split('-')
            return type === 'USER' ? { user: { id: parseInt(id) } } : { group: { id: parseInt(id) } }
          })
        }))
      }

      await api.workflows.updateCRApprovers(workflowId, updateData, true)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })

      // Show success toast
      context.service.toast.success(t('CHANGE_REQUEST_EDIT_SUCCESS_MSG'))

      hideModal()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('Failed to update workflow approvers:', error)
      const errorMessage = error?.data?.message || error?.body?.message || t('CHANGE_REQUEST_EDIT_ERROR_MSG')
      context.service.toast.error(errorMessage)
      setErrors({
        submit: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    hideModal()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  if (loading) {
    return (
      <LoadingModal
        title={t('BROWSER_FILE_SETTINGS_APPROVERS_H_TITLE')}
        onBackdropClick={handleBackdropClick}
        onClose={handleCancel}
      />
    )
  }

  return (
    <Modal show={true} handleClose={handleCancel} title={t('BROWSER_FILE_SETTINGS_APPROVERS_H_TITLE')} size="lg">
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.submit && (
            <div className="alert alert-danger" role="alert">
              {errors.submit}
            </div>
          )}

          <div className="form-group">
            <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
            <p className="form-control-static">{workflow?.workflowNo}</p>
          </div>

          <UserGroupSteps
            steps={steps}
            onChange={setSteps}
            labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
            nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
            disabled={isSubmitting}
          />
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={handleCancel} disabled={isSubmitting}>
            {t('CANCEL')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {t('SAVE')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
